import type { ExpenseCatalog } from '@clockwise/clients/expense-catalogs';
import { expensePeriodLabel } from '@clockwise/shared';
import { useState } from 'react';
import { CheckSlot, Combobox } from '@/components/custom/combobox';
import { expenseCatalogPickerConfig } from '@/components/custom/entity-pickers';
import {
  type LoadStrategy,
  useEntityAutocomplete,
} from '@/components/custom/use-entity-autocomplete';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCostOrVariable } from './expense-catalog-form';

// Picker for an expense-catalog item. See `useEntityAutocomplete` for
// `loadStrategy` semantics. Defaults to 'lazy' (server-side `q=` search per
// keystroke) for the same reason as the customer picker — the catalog is
// tenant-wide reference data that can grow past one page.
//
// The list/detail fetch + key contract lives in `entity-pickers` so this and
// any future multi-select over the same data share one React Query cache and
// can never compute divergent keys.
interface Props {
  value: string | null;
  /** Fires with the full item, so the caller can prefill description + cost
   *  from the catalog's current values. Null when cleared. */
  onChange: (item: ExpenseCatalog | null) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  loadStrategy?: LoadStrategy;
  clearable?: boolean;
  /** Label for the trigger input. The picker has no visible <label> in the
   *  edit sheet's compact layout, so pass one. */
  id?: string;
}

const { getId, getLabel, fetchList, fetchOne, listKey, detailKey, searchText } =
  expenseCatalogPickerConfig;

const filterFn = (e: ExpenseCatalog, q: string) =>
  (searchText?.(e) ?? getLabel(e)).toLowerCase().includes(q.toLowerCase());

export function ExpenseCatalogAutocomplete({
  value,
  onChange,
  disabled,
  placeholder = 'Select an expense item…',
  className,
  loadStrategy = 'lazy',
  clearable = true,
  id,
}: Props) {
  const auto = useEntityAutocomplete<ExpenseCatalog>({
    value,
    strategy: loadStrategy,
    fetchList,
    fetchOne,
    listKey,
    detailKey,
    lazyLimit: 20,
  });

  /**
   * The item the user picked here, kept so the trigger can keep showing it
   * while the hook re-resolves that same id through `fetchOne`.
   *
   * Without it the field the user just filled blanks to a skeleton for the
   * length of a round trip — they chose "Netsuite" and are left looking at a
   * grey box. Harmless when it was one of two add paths; not when it is the
   * only one, and it fires on every single add.
   *
   * Compared against `value` rather than trusted outright, so a value the
   * PARENT changed (or cleared) still resolves properly instead of showing a
   * stale name.
   */
  const [picked, setPicked] = useState<ExpenseCatalog | null>(null);
  const pickedMatchesValue = picked != null && getId(picked) === value;

  const handleChange = (item: ExpenseCatalog | null) => {
    setPicked(item);
    onChange(item);
  };

  // A picker mounted WITH a value it has never seen (the edit sheet) shows
  // nothing but its placeholder while the detail fetch is in flight, which
  // reads as "unset" rather than "loading" on a required field. Render a
  // skeleton instead — but only when the item really is unknown here.
  if (auto.resolvingValue && !pickedMatchesValue) {
    return <Skeleton className={className ?? 'h-9 w-full'} />;
  }

  return (
    <Combobox<ExpenseCatalog>
      items={auto.items}
      selectedItem={auto.selectedItem ?? (pickedMatchesValue ? picked : null)}
      value={value}
      onChange={handleChange}
      getId={getId}
      getLabel={getLabel}
      filterFn={auto.isClient ? filterFn : undefined}
      onQueryChange={auto.isClient ? undefined : auto.setQuery}
      onOpenChange={auto.setOpen}
      loading={auto.loading}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      clearable={clearable}
      id={id}
      // Item names plus their default price are wider than the trigger in a
      // sheet, so let the popover grow past the anchor. Matches the customer
      // and product pickers.
      popoverWidth="auto"
      renderItem={(e, { isSelected }) => (
        <div className="flex min-w-0 items-center gap-1.5">
          <CheckSlot isSelected={isSelected} />
          <span className="truncate font-medium">{e.name}</span>
          {/* The catalog default, shown so the user can see what selecting
              this will prefill — or, for a variable item, that there is
              nothing to prefill and the picker will hide the Cost field.
              `formatCostOrVariable` returns the raw string rather than a
              bogus $0.00 when the value isn't a finite number (G15). */}
          <span className="ml-auto shrink-0 text-xs text-muted-foreground tabular-nums">
            {formatCostOrVariable(e.cost)} · {expensePeriodLabel(e.period)}
          </span>
        </div>
      )}
    />
  );
}
